import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  SKIN_THEMES,
} from "../layouts/constants";

/* =========================================================
   MENU CONFIGURATION
========================================================= */

const MENU_CONFIG = [
  {
    key: "dashboard",
    title: "Dashboard",
    path: "/dashboard",
    moduleNames: ["Dashboard"],
  },
  {
    key: "users",
    title: "User Details",
    moduleNames: [
      "User Details",
      "Users",
      "User Management",
    ],
    children: [
      {
        title: "User List",
        path: "/user_list",
        subNames: ["User List"],
      },
      {
        title: "Add New User",
        path: "/add_user",
        subNames: ["Add New User"],
      },
      {
        title: "Sentinel User List",
        path: "/sentinel_user_list",
        subNames: ["Sentinel User List"],
      },
      {
        title: "User Incident Types",
        path: "/add_incidents",
        subNames: ["User Incident Types"],
      },
    ],
  },
  {
    key: "routes",
    title: "Route Management",
    moduleNames: [
      "Route Management",
      "Routes",
    ],
    children: [
      {
        title: "Create Route",
        path: "/create_route",
        subNames: ["Create Route"],
      },
      {
        title: "Route List",
        path: "/route_list",
        subNames: ["Route List"],
      },
      {
        title: "Place List",
        path: "/place_list",
        subNames: ["Place List"],
      },
      {
        title: "Sentinel Route List",
        path: "/sentinel_route_list",
        subNames: ["Sentinel Route List"],
      },
    ],
  },
  {
    key: "qr",
    title: "QR Codes",
    moduleNames: [
      "QR Codes",
      "QR Code Module",
      "QR Code",
      "QR",
    ],
    children: [
      {
        title: "Patrol QR",
        path: "/patrol-qr",
        subNames: ["Patrol QR"],
      },
      {
        title: "Sentinel QR",
        path: "/sentinel-qr",
        subNames: ["Sentinel QR"],
      },
    ],
  },
  {
    key: "installation",
    title: "Installation Module",
    path: "/installation",
    moduleNames: [
      "Installation Module",
      "Installation",
    ],
  },
  {
    key: "reports",
    title: "Reporting System",
    moduleNames: [
      "Reporting System",
      "Reports",
    ],
    children: [
      {
        title: "Summaries Module",
        path: "/summaries",
        subNames: ["Summaries Module"],
      },
      {
        title: "Analytics Module",
        path: "/analytics",
        subNames: ["Analytics Module"],
      },
      {
        title: "Export Module",
        path: "/export",
        subNames: ["Export Module"],
      },
      {
        title: "Custom Module",
        path: "/custom",
        subNames: ["Custom Module"],
      },
    ],
  },
  {
    key: "sentinel_modules",
    title: "Sentinel Modules",
    moduleNames: [
      "Sentinel Modules",
      "Sentinel Dashboard",
      "Sentinel Reports",
      "InCampus Module",
      "InCampus",
    ],
    children: [
      {
        title: "Sentinel Dashboard",
        path: "/sentinel_dashboard",
        subNames: ["Sentinel Dashboard"],
      },
      {
        title: "Sentinel Reports",
        path: "/sentinel_reports",
        subNames: ["Sentinel Reports"],
      },
      {
        title: "InCampus Module",
        path: "/incampus",
        subNames: [
          "InCampus Module",
          "InCampus",
        ],
      },
    ],
  },
];

/* =========================================================
   FORM CONSTANTS
========================================================= */

const ROLE_OPTIONS = [
  "Admin",
  "Supervisor",
  "Guard",
  "Manager",
];

const STATUS_OPTIONS = [
  {
    value: "pending",
    label: "Pending",
  },
  {
    value: "approved",
    label: "Approved",
  },
  {
    value: "disapproved",
    label: "Disapproved",
  },
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  designation: "",
  role: "",
  status: "pending",
  username: "",
  password: "",
  guard_seq: "",
  memberId: "",
};

/* =========================================================
   SKIN
========================================================= */

function useSkin() {
  const skinId =
    localStorage.getItem(
      "skinTheme"
    ) || "navy";

  return (
    SKIN_THEMES.find(
      (skin) =>
        skin.id === skinId
    ) || SKIN_THEMES[0]
  );
}

/* =========================================================
   PERMISSION HELPERS
========================================================= */

function normalizePermissionName(
  value
) {
  return String(
    value || ""
  )
    .trim()
    .toLowerCase()
    .replace(
      /[_-]+/g,
      " "
    )
    .replace(
      /\s+/g,
      " "
    );
}

function normalizePermissionPath(
  value
) {
  const path =
    String(
      value || ""
    )
      .trim()
      .toLowerCase()
      .split("?")[0]
      .replace(
        /\/+$/,
        ""
      );

  return path || "/";
}

function getPermissionPageId(
  item
) {
  const value =
    item?.pg_id ??
    item?.page_id ??
    item?.module_id ??
    item?.id ??
    null;

  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  return String(value);
}

function getPermissionPageName(
  item
) {
  return (
    item?.modules ||
    item?.module_name ||
    item?.page_name ||
    item?.title ||
    item?.name ||
    ""
  );
}

function getPermissionPageLink(
  item
) {
  return (
    item?.links ||
    item?.module_link ||
    item?.page_link ||
    item?.route_name ||
    item?.path ||
    ""
  );
}

function permissionNamesMatch(
  value,
  aliases = []
) {
  const normalizedValue =
    normalizePermissionName(
      value
    );

  return aliases.some(
    (alias) =>
      normalizePermissionName(
        alias
      ) === normalizedValue
  );
}

function permissionPathsMatch(
  value,
  expectedPath
) {
  if (
    !value ||
    !expectedPath
  ) {
    return false;
  }

  return (
    normalizePermissionPath(
      value
    ) ===
    normalizePermissionPath(
      expectedPath
    )
  );
}

function findPermissionPage(
  apiPages,
  aliases,
  path
) {
  return apiPages.find(
    (page) =>
      permissionNamesMatch(
        getPermissionPageName(
          page
        ),
        aliases
      ) ||
      permissionPathsMatch(
        getPermissionPageLink(
          page
        ),
        path
      )
  );
}

/**
 * The database table contains flat page rows:
 *
 * 1   Dashboard
 * 2   Installation Module
 * 101 User List
 * 102 Add New User
 * 201 Create Route
 * ...
 *
 * The parent group names are created only for display:
 *
 * User Details
 * Route Management
 * QR Codes
 * Reporting System
 * Sentinel Modules
 */
function buildMenuModules(
  payload
) {
  const apiPages =
    Array.isArray(
      payload?.modules
    )
      ? payload.modules.filter(
          (page) =>
            Number(
              page?.approve ??
                1
            ) === 1
        )
      : [];

  return MENU_CONFIG.map(
    (menuItem) => {
      const hasChildren =
        Array.isArray(
          menuItem.children
        ) &&
        menuItem.children
          .length > 0;

      /*
       * Standalone page:
       * Dashboard and Installation Module.
       */
      if (!hasChildren) {
        const matchedPage =
          findPermissionPage(
            apiPages,
            menuItem.moduleNames || [
              menuItem.title,
            ],
            menuItem.path
          );

        const permissionPageId =
          getPermissionPageId(
            matchedPage
          );

        return {
          ...(matchedPage ||
            {}),

          key:
            menuItem.key,

          page_id:
            permissionPageId ||
            `missing:${menuItem.key}`,

          permission_page_id:
            permissionPageId,

          modules:
            menuItem.title,

          links:
            menuItem.path ||
            getPermissionPageLink(
              matchedPage
            ),

          is_group:
            false,

          is_available:
            Boolean(
              permissionPageId
            ),

          submodules: [],
        };
      }

      /*
       * Group pages:
       * The parent does not have a pg_id.
       * Each child has its own real pg_id.
       */
      const submodules =
        menuItem.children.map(
          (child) => {
            const matchedPage =
              findPermissionPage(
                apiPages,
                child.subNames || [
                  child.title,
                ],
                child.path
              );

            const permissionPageId =
              getPermissionPageId(
                matchedPage
              );

            return {
              ...(matchedPage ||
                {}),

              subpage_id:
                permissionPageId ||
                `missing:${menuItem.key}:${child.path}`,

              permission_page_id:
                permissionPageId,

              submodules:
                child.title,

              links:
                child.path,

              is_available:
                Boolean(
                  permissionPageId
                ),
            };
          }
        );

      return {
        key:
          menuItem.key,

        page_id:
          `group:${menuItem.key}`,

        permission_page_id:
          null,

        modules:
          menuItem.title,

        links: "",

        is_group:
          true,

        is_available:
          submodules.some(
            (submodule) =>
              submodule
                .is_available
          ),

        submodules,
      };
    }
  );
}

function isPermissionEnabled(
  permission
) {
  return Boolean(
    permission?.main_access ??
      permission?.approve ??
      permission?.enabled ??
      permission?.access
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function AddUser() {
  const navigate =
    useNavigate();

  useSkin();

  const params =
    new URLSearchParams(
      window.location.search
    );

  const isEdit =
    params.get("mode") ===
    "edit";

  const memberIdFromQuery =
    params.get(
      "member_id"
    ) || "";

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    dashboardData,
    setDashboardData,
  ] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [
    form,
    setForm,
  ] = useState(
    initialForm
  );

  const [
    guards,
    setGuards,
  ] = useState([]);

  const [
    staffDbConnected,
    setStaffDbConnected,
  ] = useState(true);

  const [
    modules,
    setModules,
  ] = useState([]);

  const [
    selectedModules,
    setSelectedModules,
  ] = useState({});

  const [
    selectedSubmodules,
    setSelectedSubmodules,
  ] = useState({});

  const [
    licensedUsers,
    setLicensedUsers,
  ] = useState(0);

  const [
    currentUsers,
    setCurrentUsers,
  ] = useState(0);

  const [
    limitReached,
    setLimitReached,
  ] = useState(false);

  /* =======================================================
     INITIAL PAGE LOAD
  ======================================================= */

  useEffect(() => {
    const token =
      localStorage.getItem(
        "token"
      ) ||
      localStorage.getItem(
        "authToken"
      );

    const isLoggedIn =
      localStorage.getItem(
        "isLoggedIn"
      );

    let userData = {};

    try {
      userData =
        JSON.parse(
          localStorage.getItem(
            "userData"
          ) || "{}"
        );
    } catch (error) {
      console.error(
        "Invalid userData:",
        error
      );

      userData = {};
    }

    if (
      !token ||
      !isLoggedIn
    ) {
      navigate("/", {
        replace: true,
      });

      return;
    }

    const local = {
      company_name:
        localStorage.getItem(
          "company_name"
        ) ||
        userData.company_name ||
        "",

      licensedFor:
        localStorage.getItem(
          "licensedFor"
        ) ||
        userData.licensedFor ||
        "",

      admin_active:
        Number(
          localStorage.getItem(
            "admin_active"
          ) ??
            userData.admin_active ??
            0
        ),

      company_address:
        localStorage.getItem(
          "company_address"
        ) ||
        userData.company_address ||
        "",

      company_phone:
        localStorage.getItem(
          "company_phone"
        ) ||
        userData.company_phone ||
        "",

      admin_email:
        localStorage.getItem(
          "admin_email"
        ) ||
        userData.admin_email ||
        "",

      company_logo:
        localStorage.getItem(
          "company_logo"
        ) ||
        userData.company_logo ||
        "",

      user_map:
        localStorage.getItem(
          "user_map"
        ) ||
        userData.user_map ||
        "",
    };

    setDashboardData(
      local
    );

    fetchPageData(
      local,
      token
    );
  }, [
    navigate,
    isEdit,
    memberIdFromQuery,
  ]);

  /* =======================================================
     GUARD NORMALIZATION
  ======================================================= */

  function normalizeGuards(
    payload
  ) {
    const list =
      payload?.guards ||
      payload?.staff ||
      payload?.payrollStaff ||
      payload?.data
        ?.guards ||
      [];

    return Array.isArray(
      list
    )
      ? list
      : [];
  }

  /* =======================================================
     FETCH PAGE DATA
  ======================================================= */

  async function fetchPageData(
    local,
    token
  ) {
    try {
      setLoading(true);

      const dashboardUrl =
        `${API_BASE_URL}/dashboard/admin/dashboard`;

      const formUrl =
        `${API_BASE_URL}/users/form-init${
          isEdit
            ? `?mode=edit&member_id=${encodeURIComponent(
                memberIdFromQuery
              )}`
            : ""
        }`;

      const [
        dashboardRes,
        formRes,
      ] = await Promise.all([
        fetch(
          dashboardUrl,
          {
            method: "GET",
            cache: "no-store",
            headers: {
              Accept:
                "application/json",

              Authorization:
                `Bearer ${token}`,

              "Cache-Control":
                "no-cache, no-store, must-revalidate",

              Pragma:
                "no-cache",

              Expires:
                "0",
            },
          }
        ),

        fetch(
          formUrl,
          {
            method: "GET",
            cache: "no-store",
            headers: {
              Accept:
                "application/json",

              Authorization:
                `Bearer ${token}`,

              "Cache-Control":
                "no-cache, no-store, must-revalidate",

              Pragma:
                "no-cache",

              Expires:
                "0",
            },
          }
        ),
      ]);

      if (
        dashboardRes.status ===
          401 ||
        dashboardRes.status ===
          403 ||
        formRes.status === 401 ||
        formRes.status === 403
      ) {
        localStorage.clear();

        navigate("/", {
          replace: true,
        });

        return;
      }

      const dashboardResult =
        await dashboardRes.json();

      const formResult =
        await formRes.json();

      if (
        !dashboardRes.ok
      ) {
        throw new Error(
          dashboardResult
            ?.message ||
            "Failed to fetch dashboard"
        );
      }

      if (!formRes.ok) {
        throw new Error(
          formResult
            ?.message ||
            "Failed to fetch user form data"
        );
      }

      const companyDetails =
        dashboardResult
          ?.data
          ?.companyDetails ||
        {};

      const updatedDashboard =
        {
          ...local,

          company_name:
            companyDetails
              .company_name ??
            local.company_name ??
            "",

          licensedFor:
            companyDetails
              .licensedFor ??
            local.licensedFor ??
            "",

          admin_active:
            Number(
              companyDetails
                .admin_active ??
                local.admin_active ??
                0
            ),

          company_address:
            companyDetails
              .company_address ??
            local.company_address ??
            "",

          company_phone:
            companyDetails
              .company_phone ??
            local.company_phone ??
            "",

          admin_email:
            companyDetails
              .admin_email ??
            local.admin_email ??
            "",

          company_logo:
            companyDetails
              .company_logo ??
            local.company_logo ??
            "",

          user_map:
            companyDetails
              .user_map ??
            local.user_map ??
            "",
        };

      setDashboardData(
        updatedDashboard
      );

      const payload =
        formResult?.data ||
        {};

      const guardList =
        normalizeGuards(
          payload
        );

      console.log(
        "Payroll guards from /users/form-init:",
        guardList
      );

      setLicensedUsers(
        Number(
          payload.licensedUsers ||
            0
        )
      );

      setCurrentUsers(
        Number(
          payload.currentUsers ||
            0
        )
      );

      setLimitReached(
        Boolean(
          payload.limitReached
        )
      );

      setGuards(
        guardList
      );

      setStaffDbConnected(
        payload.staffDbConnected !==
          false
      );

      /*
       * Build the screen structure from MENU_CONFIG,
       * while preserving the real pg_id values.
       */
      const modulesWithSubs =
        buildMenuModules(
          payload
        );

      setModules(
        modulesWithSubs
      );

      const vm =
        payload.vm || {};

      // Normalize phone: strip +91 / 91 / non-digits, keep last 10 digits for the input
      const rawPhone = String(vm.phone || "").replace(/\D/g, "");
      const normalizedPhone =
        rawPhone.length > 10 &&
        (rawPhone.startsWith("91") || rawPhone.startsWith("0"))
          ? rawPhone.slice(-10)
          : rawPhone.slice(0, 10);

      setForm({
        ...initialForm,

        fullName:
          vm.fullName ||
          "",

        email:
          vm.email ||
          "",

        phone: normalizedPhone,

        designation:
          vm.designation ||
          "",

        role:
          vm.role ||
          "",

        status:
          vm.status ||
          "pending",

        username:
          vm.username ||
          "",

        password: "",

        guard_seq:
          vm.guard_seq ===
            null ||
          vm.guard_seq ===
            undefined
            ? ""
            : String(
                vm.guard_seq
              ),

        memberId:
          vm.memberId ||
          payload.nextMemberId ||
          "",
      });

      /*
       * Existing permissions are expected to be keyed
       * by the real pg_id:
       *
       * existingPermissions["101"]
       * existingPermissions["201"]
       * etc.
       */
      const existingPermissions =
        payload
          .existingPermissions ||
        {};

      const modulesState =
        {};

      const submodulesState =
        {};

      modulesWithSubs.forEach(
        (module) => {
          const moduleKey =
            String(
              module.page_id
            );

          /*
           * Parent group:
           * Check every child pg_id.
           */
          if (
            module.is_group
          ) {
            const selectedChildIds =
              module.submodules
                .filter(
                  (
                    submodule
                  ) => {
                    const permissionId =
                      String(
                        submodule
                          .permission_page_id ||
                          ""
                      );

                    if (
                      !permissionId
                    ) {
                      return false;
                    }

                    return isPermissionEnabled(
                      existingPermissions?.[
                        permissionId
                      ]
                    );
                  }
                )
                .map(
                  (
                    submodule
                  ) =>
                    String(
                      submodule
                        .permission_page_id
                    )
                );

            modulesState[
              moduleKey
            ] =
              selectedChildIds
                .length > 0;

            submodulesState[
              moduleKey
            ] =
              selectedChildIds;

            return;
          }

          /*
           * Standalone page:
           * Dashboard or Installation Module.
           */
          const permissionId =
            String(
              module
                .permission_page_id ||
                ""
            );

          modulesState[
            moduleKey
          ] =
            permissionId
              ? isPermissionEnabled(
                  existingPermissions?.[
                    permissionId
                  ]
                )
              : false;

          submodulesState[
            moduleKey
          ] = [];
        }
      );

      setSelectedModules(
        modulesState
      );

      setSelectedSubmodules(
        submodulesState
      );
    } catch (error) {
      console.error(
        "Add user fetch error:",
        error
      );

      toast.error(
        error.message ||
          "Failed to load page data."
      );
    } finally {
      setLoading(false);
    }
  }

  /* =======================================================
     MEMOS
  ======================================================= */

  const routePage =
    useMemo(
      () =>
        dashboardData.user_map ===
        "google"
          ? "/route-master"
          : "/route-master1",
      [
        dashboardData
          .user_map,
      ]
    );

  const remainingUsers =
    useMemo(() => {
      if (
        licensedUsers > 0
      ) {
        return Math.max(
          0,
          licensedUsers -
            currentUsers
        );
      }

      return null;
    }, [
      licensedUsers,
      currentUsers,
    ]);

  /* =======================================================
     INPUT HANDLER
  ======================================================= */

  function handleChange(
    event
  ) {
    const {
      name,
      value,
    } = event.target;

    // Phone: store only digits, max 10 (country code +91 is fixed separately)
    if (name === "phone") {
      const digitsOnly = String(value)
        .replace(/\D/g, "")
        .slice(0, 10);

      setForm((previous) => ({
        ...previous,
        phone: digitsOnly,
      }));
      return;
    }

    setForm(
      (previous) => ({
        ...previous,
        [name]: value,
      })
    );
  }

  function isValidEmail(email) {
    const value = String(email || "").trim();
    // Must contain @ and at least one . after the @
    const atIndex = value.indexOf("@");
    if (atIndex < 1) return false;
    const domain = value.slice(atIndex + 1);
    if (!domain.includes(".")) return false;
    // Basic structure: local@domain.tld (no spaces)
    if (/\s/.test(value)) return false;
    if (value.startsWith(".") || value.endsWith(".")) return false;
    if (value.includes("..")) return false;
    return true;
  }

  function isValidPhone(phone) {
    // Exactly 10 digits (excluding the fixed +91 prefix)
    return /^\d{10}$/.test(String(phone || "").trim());
  }

  /* =======================================================
     MODULE TOGGLE
  ======================================================= */

  function toggleModule(
    module
  ) {
    const moduleKey =
      String(
        module.page_id
      );

    const nextEnabled =
      !selectedModules[
        moduleKey
      ];

    setSelectedModules(
      (previous) => ({
        ...previous,
        [moduleKey]:
          nextEnabled,
      })
    );

    /*
     * Enabling a group selects all available child pages.
     * Disabling the group clears all child pages.
     */
    if (
      module.is_group
    ) {
      const availableChildIds =
        module.submodules
          .filter(
            (
              submodule
            ) =>
              submodule
                .is_available &&
              submodule
                .permission_page_id
          )
          .map(
            (
              submodule
            ) =>
              String(
                submodule
                  .permission_page_id
              )
          );

      setSelectedSubmodules(
        (previous) => ({
          ...previous,

          [moduleKey]:
            nextEnabled
              ? availableChildIds
              : [],
        })
      );

      return;
    }

    if (!nextEnabled) {
      setSelectedSubmodules(
        (previous) => ({
          ...previous,
          [moduleKey]: [],
        })
      );
    }
  }

  /* =======================================================
     SUBMODULE TOGGLE
  ======================================================= */

  function toggleSubmodule(
    module,
    submodule
  ) {
    if (
      !submodule
        .is_available ||
      !submodule
        .permission_page_id
    ) {
      return;
    }

    const moduleKey =
      String(
        module.page_id
      );

    const permissionId =
      String(
        submodule
          .permission_page_id
      );

    setSelectedSubmodules(
      (previous) => {
        const current =
          previous[
            moduleKey
          ] || [];

        const exists =
          current.includes(
            permissionId
          );

        const next =
          exists
            ? current.filter(
                (item) =>
                  item !==
                  permissionId
              )
            : [
                ...current,
                permissionId,
              ];

        /*
         * Parent checkbox remains enabled while
         * at least one child is selected.
         */
        setSelectedModules(
          (
            oldModules
          ) => ({
            ...oldModules,

            [moduleKey]:
              next.length >
              0,
          })
        );

        return {
          ...previous,
          [moduleKey]:
            next,
        };
      }
    );
  }

  /* =======================================================
     BUILD REQUEST PAYLOAD
  ======================================================= */

  function buildPayload() {
    const perm_module =
      {};

    const perm_sub =
      {};

    modules.forEach(
      (module) => {
        const moduleKey =
          String(
            module.page_id
          );

        /*
         * Group parent has no database pg_id.
         * Save each selected child pg_id separately.
         */
        if (
          module.is_group
        ) {
          const selectedIds =
            selectedSubmodules[
              moduleKey
            ] || [];

          selectedIds.forEach(
            (
              permissionId
            ) => {
              const id =
                String(
                  permissionId ||
                    ""
                );

              if (
                !id ||
                id.startsWith(
                  "missing:"
                )
              ) {
                return;
              }

              perm_module[
                id
              ] = true;

              perm_sub[
                id
              ] = [];
            }
          );

          return;
        }

        /*
         * Standalone module:
         * Save its real database pg_id.
         */
        const enabled =
          Boolean(
            selectedModules[
              moduleKey
            ]
          );

        const permissionId =
          String(
            module
              .permission_page_id ||
              ""
          );

        if (
          !enabled ||
          !permissionId
        ) {
          return;
        }

        perm_module[
          permissionId
        ] = true;

        perm_sub[
          permissionId
        ] = [];
      }
    );

    const payload = {
      form_mode:
        isEdit
          ? "edit"
          : "create",

      fullName:
        form.fullName.trim(),

      email:
        form.email.trim(),

      // Always store with fixed +91 country code; form.phone is 10 digits only
      phone:
        form.phone.trim()
          ? `+91${form.phone.trim()}`
          : "",

      designation:
        form.designation.trim(),

      role:
        form.role.trim(),

      status:
        form.status,

      username:
        form.username.trim(),

      password:
        form.password,

      guard_seq:
        form.guard_seq ===
          ""
          ? null
          : Number(
              form.guard_seq
            ),

      memberId:
        form.memberId,

      perm_module,

      perm_sub,
    };

    /*
     * During edit, an empty password means
     * keep the current password.
     */
    if (
      isEdit &&
      !form.password.trim()
    ) {
      delete payload.password;
    }

    return payload;
  }

  /* =======================================================
     SUBMIT
  ======================================================= */

  async function handleSubmit(
    event
  ) {
    event.preventDefault();

    if (
      !form.fullName
        .trim() ||
      !form.username
        .trim() ||
      !form.role
        .trim() ||
      !form.email.trim() ||
      !form.phone.trim()
    ) {
      toast.error(
        "Please fill all required fields."
      );

      return;
    }

    if (!isValidEmail(form.email)) {
      toast.error(
        "Please enter a valid email address (must include @ and a domain with .)."
      );
      return;
    }

    if (!isValidPhone(form.phone)) {
      toast.error(
        "Phone number must be exactly 10 digits (excluding +91)."
      );
      return;
    }

    if (
      !isEdit &&
      !form.password
        .trim()
    ) {
      toast.error(
        "Password is required."
      );

      return;
    }

    if (
      !isEdit &&
      limitReached
    ) {
      toast.error(
        `User limit reached (${currentUsers} of ${licensedUsers}). You cannot add more users.`
      );

      return;
    }

    try {
      setSaving(true);

      const token =
        localStorage.getItem(
          "token"
        ) ||
        localStorage.getItem(
          "authToken"
        );

      if (!token) {
        navigate("/", {
          replace: true,
        });

        return;
      }

      const response =
        await fetch(
          `${API_BASE_URL}/users/${
            isEdit
              ? "update"
              : "create"
          }`,
          {
            method: "POST",

            cache: "no-store",

            headers: {
              "Content-Type":
                "application/json",

              Authorization:
                `Bearer ${token}`,

              "Cache-Control":
                "no-cache, no-store, must-revalidate",

              Pragma:
                "no-cache",

              Expires:
                "0",
            },

            body:
              JSON.stringify(
                buildPayload()
              ),
          }
        );

      const data =
        await response.json();

      if (
        !response.ok ||
        data.success ===
          false
      ) {
        throw new Error(
          data.message ||
            "Failed to save user"
        );
      }

      toast.success(
        isEdit
          ? "User updated successfully!"
          : "User created successfully!"
      );

      setTimeout(() => {
        navigate(
          "/user_list"
        );
      }, 1000);
    } catch (error) {
      console.error(
        "Save user error:",
        error
      );

      toast.error(
        error.message ||
          "Failed to save user."
      );
    } finally {
      setSaving(false);
    }
  }

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />

        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading add user page...
        </p>
      </div>
    );
  }

  /* =======================================================
     PAGE
  ======================================================= */

  return (
    <Layout
      dashboardData={
        dashboardData
      }
      routePage={
        routePage
      }
      activePage="add_user"
      activeMenu="users"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,

          style: {
            background:
              "var(--card-bg)",

            color:
              "var(--text-primary)",

            border:
              "1px solid var(--card-border)",

            borderRadius:
              "12px",

            padding:
              "16px 20px",

            fontSize:
              "13px",

            fontWeight:
              "600",

            boxShadow:
              "0 8px 32px rgba(0,0,0,0.3)",
          },

          success: {
            iconTheme: {
              primary:
                "#22c55e",

              secondary:
                "white",
            },
          },

          error: {
            iconTheme: {
              primary:
                "#ef4444",

              secondary:
                "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        {/* ===============================================
            PAGE HEADER
        =============================================== */}

        <div className="mb-1">
          <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
            User Management
          </h1>

          <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
            {isEdit
              ? "Update existing user details and permissions"
              : "Create a new user and assign permissions"}
          </p>
        </div>

        {/* ===============================================
            TOP CARD
        =============================================== */}

        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px] mt-[22px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />

          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                {isEdit
                  ? "Edit User"
                  : "Add New User"}
              </h2>

              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Fill the form below to{" "}
                {isEdit
                  ? "update the account"
                  : "create the account"}
              </p>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate(
                  "/user_list"
                )
              }
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background:
                  "var(--btn-bg)",

                color:
                  "var(--btn-text)",

                boxShadow:
                  "0 4px 18px var(--accent-glow)",
              }}
            >
              Back to User List
            </button>
          </div>
        </div>

        {/* ===============================================
            LICENSE BANNER
        =============================================== */}

        <div
          className={`mb-[18px] px-4 py-3.5 rounded-xl text-[13px] font-bold border ${
            limitReached
              ? "bg-[var(--red-faint)] border-[var(--red-border)] text-[var(--red)]"
              : "bg-[rgba(34,197,94,0.12)] border-[rgba(34,197,94,0.28)] text-[#22c55e]"
          }`}
        >
          <span className="font-extrabold">
            User Capacity:
          </span>{" "}

          {licensedUsers > 0
            ? `${currentUsers} / ${licensedUsers}`
            : `${currentUsers} (no limit set)`}

          {" · "}

          {limitReached
            ? "Limit reached — new user creation is disabled."
            : licensedUsers >
                0
              ? `You can add ${remainingUsers} user(s).`
              : "You can add more user(s)."}
        </div>

        <form
          onSubmit={
            handleSubmit
          }
        >
          {/* =============================================
              BASIC INFORMATION
          ============================================= */}

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <div className="mb-3.5">
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Basic Information
              </h3>

              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                {isEdit
                  ? "Modify user profile details"
                  : "Enter the user's profile details"}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Full Name

                  <span className="text-[var(--red)] ml-0.5">
                    *
                  </span>
                </label>

                <input
                  name="fullName"
                  value={
                    form.fullName
                  }
                  onChange={
                    handleChange
                  }
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Email

                  <span className="text-[var(--red)] ml-0.5">
                    *
                  </span>

                </label>

                <input
                  type="email"
                  name="email"
                  value={
                    form.email
                  }
                  onChange={
                    handleChange
                  }
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Phone
                  <span className="text-[var(--red)] ml-0.5">
                    *
                  </span>
                </label>

                <div className="flex items-stretch rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] overflow-hidden focus-within:border-[var(--accent)] focus-within:shadow-[0_0_0_3px_var(--accent-glow)] transition-colors duration-150">
                  <span className="inline-flex items-center px-3.5 text-[13px] font-extrabold text-[var(--text-sub)] bg-[var(--card-bg)] border-r border-[var(--surface-border)] select-none">
                    +91
                  </span>
                  <input
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    value={
                      form.phone
                    }
                    onChange={
                      handleChange
                    }
                    required
                    className="min-w-0 flex-1 border-0 bg-transparent text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
                    placeholder="10-digit mobile number"
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-[var(--text-muted)] m-0">
                  Country code +91 is fixed. Enter exactly 10 digits.
                </p>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Designation
                </label>

                <input
                  name="designation"
                  value={
                    form.designation
                  }
                  onChange={
                    handleChange
                  }
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder="Enter designation"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Role

                  <span className="text-[var(--red)] ml-0.5">
                    *
                  </span>
                </label>

                <select
                  name="role"
                  value={
                    form.role
                  }
                  onChange={
                    handleChange
                  }
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                >
                  <option value="">
                    Select role
                  </option>

                  {ROLE_OPTIONS.map(
                    (role) => (
                      <option
                        key={role}
                        value={role}
                      >
                        {role}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Status
                </label>

                <select
                  name="status"
                  value={
                    form.status
                  }
                  onChange={
                    handleChange
                  }
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                >
                  {STATUS_OPTIONS.map(
                    (status) => (
                      <option
                        key={
                          status.value
                        }
                        value={
                          status.value
                        }
                      >
                        {
                          status.label
                        }
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
          </div>

          {/* =============================================
              LOGIN CREDENTIALS
          ============================================= */}

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <div className="mb-3.5">
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Login Credentials
              </h3>

              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Set username, password, and payroll staff mapping
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Username

                  <span className="text-[var(--red)] ml-0.5">
                    *
                  </span>
                </label>

                <input
                  name="username"
                  value={
                    form.username
                  }
                  onChange={
                    handleChange
                  }
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder="Enter username"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  {isEdit
                    ? "Password (leave blank to keep current)"
                    : "Password *"}
                </label>

                <input
                  type="password"
                  name="password"
                  value={
                    form.password
                  }
                  onChange={
                    handleChange
                  }
                  required={
                    !isEdit
                  }
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder={
                    isEdit
                      ? "Leave blank to keep current"
                      : "Enter password"
                  }
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Payroll Staff / Guard
                </label>

                <select
                  name="guard_seq"
                  value={
                    form.guard_seq
                  }
                  onChange={
                    handleChange
                  }
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                >
                  <option value="">
                    -- Select Payroll Staff --
                  </option>

                  {!staffDbConnected ? (
                    <option
                      value=""
                      disabled
                    >
                      Payroll DB connection failed
                    </option>
                  ) : guards.length ===
                    0 ? (
                    <option
                      value=""
                      disabled
                    >
                      No payroll staff found
                    </option>
                  ) : (
                    guards.map(
                      (
                        guard,
                        index
                      ) => {
                        const seq =
                          guard.staff_seq ??
                          guard.staffSeq ??
                          guard.seq ??
                          guard.id ??
                          guard.staff_id ??
                          "";

                        const name =
                          guard.staff_name ||
                          guard.staffName ||
                          guard.name ||
                          guard.employee_name ||
                          guard.full_name ||
                          "";

                        const applicationNumber =
                          guard.application_number ||
                          guard.applicationNumber ||
                          guard.app_no ||
                          guard.appNo ||
                          "";

                        const label =
                          `${name}${
                            applicationNumber
                              ? ` (${applicationNumber})`
                              : ""
                          }`;

                        return (
                          <option
                            key={
                              seq ||
                              index
                            }
                            value={
                              String(
                                seq
                              )
                            }
                          >
                            {label ||
                              `Staff ${
                                index +
                                1
                              }`}
                          </option>
                        );
                      }
                    )
                  )}
                </select>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Member ID
                </label>

                <input
                  name="memberId"
                  value={
                    form.memberId
                  }
                  readOnly
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                  placeholder="Auto-generated"
                />
              </div>
            </div>
          </div>

          {/* =============================================
              MODULE ACCESS
          ============================================= */}

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <div className="mb-3.5">
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Module Access & Permissions
              </h3>

              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Toggle modules and choose submodule access
              </p>
            </div>

            <div className="space-y-3.5">
              {modules.length ===
              0 ? (
                <div className="py-4 px-4 rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] font-semibold text-center">
                  No modules found.
                </div>
              ) : (
                modules.map(
                  (module) => {
                    const pageId =
                      String(
                        module.page_id
                      );

                    const enabled =
                      Boolean(
                        selectedModules[
                          pageId
                        ]
                      );

                    const selectedSubs =
                      selectedSubmodules[
                        pageId
                      ] || [];

                    const subs =
                      module.submodules ||
                      [];

                    return (
                      <div
                        key={
                          pageId
                        }
                        className="border border-[var(--surface-border)] rounded-xl overflow-hidden bg-[var(--card-bg)]"
                      >
                        <div
                          className="px-5 py-4 flex justify-between items-center gap-3 flex-wrap"
                          style={{
                            background:
                              "var(--btn-bg)",
                          }}
                        >
                          <div className="text-[15px] font-extrabold text-[var(--btn-text)]">
                            {
                              module.modules
                            }
                          </div>

                          <label
                            className={`flex items-center gap-2.5 text-[12px] font-bold text-[var(--btn-text)] ${
                              module
                                .is_available
                                ? "cursor-pointer"
                                : "cursor-not-allowed opacity-60"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={
                                enabled
                              }
                              disabled={
                                !module
                                  .is_available
                              }
                              onChange={() =>
                                toggleModule(
                                  module
                                )
                              }
                              className="w-[18px] h-[18px] accent-[var(--accent)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                            />

                            Enable Module
                          </label>
                        </div>

                        <div className="p-5">
                          {!module
                            .is_available && (
                            <div className="mb-3 py-3 px-4 rounded-xl border border-[var(--red-border)] bg-[var(--red-faint)] text-[var(--red)] text-[12px] font-bold">
                              This module or its pages were not found in the backend module data.
                            </div>
                          )}

                          {subs.length ===
                          0 ? (
                            <div className="py-4 px-4 rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] font-semibold text-center">
                              No sub-modules. Enabling grants full access.
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
                              {subs.map(
                                (
                                  submodule
                                ) => {
                                  const permissionId =
                                    String(
                                      submodule
                                        .permission_page_id ||
                                        ""
                                    );

                                  const checked =
                                    selectedSubs.includes(
                                      permissionId
                                    );

                                  const canSelect =
                                    enabled &&
                                    submodule
                                      .is_available &&
                                    Boolean(
                                      submodule
                                        .permission_page_id
                                    );

                                  return (
                                    <label
                                      key={`${pageId}-${submodule.subpage_id}`}
                                      className={`flex items-start gap-2.5 border border-[var(--surface-border)] rounded-xl p-3 ${
                                        canSelect
                                          ? "bg-[var(--surface)] cursor-pointer hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                                          : "bg-[var(--surface)] opacity-50 cursor-not-allowed"
                                      } transition-colors duration-150`}
                                    >
                                      <input
                                        type="checkbox"
                                        disabled={
                                          !canSelect
                                        }
                                        checked={
                                          checked
                                        }
                                        onChange={() =>
                                          toggleSubmodule(
                                            module,
                                            submodule
                                          )
                                        }
                                        className="mt-1 w-4 h-4 accent-[var(--accent)] flex-shrink-0 disabled:cursor-not-allowed"
                                      />

                                      <div>
                                        <div className="text-[13px] font-bold text-[var(--text-primary)]">
                                          {
                                            submodule.submodules
                                          }
                                        </div>

                                        {submodule.links && (
                                          <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">
                                            {
                                              submodule.links
                                            }
                                          </div>
                                        )}

                                        {!submodule
                                          .is_available && (
                                          <div className="mt-1 text-[10px] font-bold text-[var(--red)]">
                                            Missing from backend module data
                                          </div>
                                        )}
                                      </div>
                                    </label>
                                  );
                                }
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                )
              )}
            </div>
          </div>

          {/* =============================================
              ACTIONS
          ============================================= */}

          <div className="flex justify-end gap-3 flex-wrap mt-1.5 mb-3">
            <button
              type="button"
              onClick={() =>
                navigate(
                  "/user_list"
                )
              }
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
            >
              Back to User List
            </button>

            <button
              type="submit"
              disabled={
                saving ||
                (!isEdit &&
                  limitReached)
              }
              className="border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              style={{
                background:
                  "var(--btn-bg)",

                color:
                  "var(--btn-text)",

                boxShadow:
                  "0 4px 18px var(--accent-glow)",
              }}
            >
              {saving
                ? "Saving..."
                : isEdit
                  ? "Update User"
                  : "+ Create User"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}